import type { IWorkJourneyMatrixCell } from '@src/models/interfaces/workJourneyManagement.interface';

import { isReplacementCell } from './isReplacementCell';

export type MatrixRemoveExceptionCopyKey =
  | 'vacation'
  | 'justifiedAbsence'
  | 'unjustifiedAbsence'
  | 'medicalLeave'
  | 'replacement'
  | 'absence';

type RemovableCellFields = Pick<
  IWorkJourneyMatrixCell,
  'kind' | 'code' | 'exceptionId' | 'exceptionType'
>;

/**
 * Absence / leave exceptions that the matrix can delete (selection bar + info drawer).
 * Replacements are also removable exceptions but use a dedicated undo path in the info drawer.
 */
export const isAbsenceLikeMatrixExceptionCell = (
  cell?: RemovableCellFields | null,
): boolean => {
  if (!cell?.exceptionId) return false;
  if (isReplacementCell(cell)) return false;
  return (
    cell.kind === 'justified_absence' ||
    cell.kind === 'unjustified_absence' ||
    cell.kind === 'vacation' ||
    cell.kind === 'medical_leave' ||
    cell.kind === 'other_absence' ||
    cell.code === 'AUJ' ||
    cell.code === 'AUI' ||
    cell.code === 'VAC' ||
    cell.code === 'LM' ||
    cell.code === 'PGR' ||
    cell.code === 'PSG' ||
    cell.code === 'ABS'
  );
};

/** True when the selection bar may offer remove for this cell. */
export const isRemovableMatrixExceptionCell = (cell?: RemovableCellFields | null): boolean => {
  if (!cell?.exceptionId) return false;
  return isAbsenceLikeMatrixExceptionCell(cell) || isReplacementCell(cell);
};

/** User-facing remove copy family for the selected exception cell. */
export const resolveMatrixRemoveExceptionCopyKey = (
  cell?: RemovableCellFields | null,
): MatrixRemoveExceptionCopyKey | null => {
  if (!isRemovableMatrixExceptionCell(cell)) return null;
  if (isReplacementCell(cell)) return 'replacement';
  if (cell?.kind === 'vacation' || cell?.code === 'VAC' || cell?.exceptionType === 'vacation') {
    return 'vacation';
  }
  if (
    cell?.kind === 'unjustified_absence' ||
    cell?.code === 'AUI' ||
    cell?.exceptionType === 'unjustified_absence'
  ) {
    return 'unjustifiedAbsence';
  }
  if (
    cell?.kind === 'medical_leave' ||
    cell?.code === 'LM' ||
    cell?.exceptionType === 'medical_leave'
  ) {
    return 'medicalLeave';
  }
  if (
    cell?.kind === 'justified_absence' ||
    cell?.code === 'AUJ' ||
    cell?.code === 'PGR' ||
    cell?.code === 'PSG' ||
    cell?.exceptionType === 'justified_absence' ||
    cell?.exceptionType === 'pregnancy_leave' ||
    cell?.exceptionType === 'parental_leave'
  ) {
    return 'justifiedAbsence';
  }
  return 'absence';
};

export const matrixRemoveExceptionLabelId = (key: MatrixRemoveExceptionCopyKey): string =>
  `workJourney.management.matrix.selectionBar.remove.${key}`;

export const matrixRemoveExceptionConfirmTitleId = (key: MatrixRemoveExceptionCopyKey): string =>
  `workJourney.management.matrix.selectionBar.remove.${key}.confirmTitle`;
